// Minimal bot signatures for basic bot/human traffic analytics - not a
// security-grade classifier (a user agent is trivially spoofed). Kept free of
// server-only imports because the browser Sentry setup uses it too (CON-9073).
//
// Each entry is [botMarker, botName]. A marker is matched with `.includes()`
// against the lower-cased user agent, so any user agent that contains it counts
// as that bot: for example anything containing `curl/` is identified as curl,
// whatever else it says. Keep markers specific enough not to appear in a real
// browser's user agent.
//
// Where the list comes from: it is not taken from a third-party library or
// published bot list. The first entries were written by hand for the request
// log (INFRA-559). The Meta entries come from the user agents of the crawlers
// behind the CON-9073 Sentry errors, seen in Sentry tags and in Dash0 and
// CloudFront data. To maintain it, add a marker when a new crawler shows up in
// the request log's `http.user_agent` field or in Dash0, and note the ticket.
const BOT_SIGNATURES = [
  ['googlebot', 'Googlebot'],
  ['bingbot', 'Bingbot'],
  ['yandexbot', 'YandexBot'],
  ['duckduckbot', 'DuckDuckBot'],
  ['baiduspider', 'Baiduspider'],
  ['twitterbot', 'Twitterbot'],
  ['facebookexternalhit', 'Facebook'],
  ['facebot', 'Facebook'],
  // Meta's renderers send a Chrome user agent with a `meta-...` token, which
  // the generic pattern below does not match.
  ['meta-webindexer', 'Meta Webindexer'],
  ['meta-externalads', 'Meta ExternalAds'],
  ['meta-externalagent', 'Meta ExternalAgent'],
  ['gptbot', 'GPTBot'],
  ['ccbot', 'CCBot'],
  ['ahrefsbot', 'AhrefsBot'],
  ['semrushbot', 'SemrushBot'],
  ['applebot', 'Applebot'],
  ['slurp', 'Yahoo Slurp'],
  ['curl/', 'curl'],
  ['python-requests', 'python-requests'],
  ['okhttp', 'okhttp'],
];

const GENERIC_BOT_PATTERN = /bot|spider|crawler/;

/**
 * Classifies a User-Agent string for basic bot analytics. Returns
 * { isBot: boolean, botName: string|null }.
 */
export const classifyUserAgent = userAgent => {
  if (!userAgent || typeof userAgent !== 'string') {
    return { isBot: false, botName: null };
  }

  const ua = userAgent.toLowerCase();
  const signature = BOT_SIGNATURES.find(([botMarker]) =>
    ua.includes(botMarker)
  );

  if (signature) {
    const [, botName] = signature;
    return { isBot: true, botName };
  }

  if (GENERIC_BOT_PATTERN.test(ua)) {
    return { isBot: true, botName: 'Generic Bot' };
  }

  return { isBot: false, botName: null };
};

export default classifyUserAgent;
